import { useEffect, useMemo, useState } from "react";
import { apiStaff } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Plus, PhoneCall, CheckCircle2, MessageCircle, DollarSign,
  ClipboardList, X, Search, MapPin, Mail, Clock, Repeat, ChevronRight, History, CalendarDays,
} from "lucide-react";
import {
  PortalShell, TopBar, Sidebar, MobileDrawer, useMobileDrawer, PageContent, PageHeader,
  Card, StatusPill, EmptyState, Loading, BrandMark,
} from "@/components/admin";
import DispatchBoardDnD from "@/components/admin/DispatchBoardDnD";
import DispatchCalendar from "@/components/admin/DispatchCalendar";
import RecurringPlans from "@/components/admin/RecurringPlans";
import { useRealtime } from "@/lib/realtime";
import { JobDrawer, NewJobDialog } from "@/pages/portals/CrmPortal";

const APPLIANCES = ["washer","dryer","refrigerator","oven","stove","dishwasher","microwave","disposal","ice-maker","other"];

const NAV = [
  { key: "board", label: "Board", icon: ClipboardList },
  { key: "calendar", label: "Calendar", icon: CalendarDays },
  { key: "search", label: "Search", icon: Search },
  { key: "callbacks", label: "Callbacks", icon: PhoneCall },
  { key: "recurring", label: "Recurring", icon: Repeat },
  { key: "prices", label: "Price guide", icon: DollarSign },
];

export default function DispatchPortal({ info, onLogout }) {
  const [tab, setTab] = useState("board");
  const [composerOpen, setComposerOpen] = useState(false);
  const [newJobOpen, setNewJobOpen] = useState(false);
  const [newJobPrefill, setNewJobPrefill] = useState(null);
  const [calJob, setCalJob] = useState(null);
  const [counts, setCounts] = useState({});
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    apiStaff.get("/crm/missed-calls?status=pending").then(r => setCounts({ callbacks: r.data.length })).catch(() => setCounts({}));
  }, [tab]);

  const navWithCounts = NAV.map((it) => ({
    ...it,
    badge: it.key === "callbacks" ? counts.callbacks : null,
  }));
  const drawer = useMobileDrawer();
  const activeNav = navWithCounts.find((n) => n.key === tab);

  function openNewJob(prefill = null) {
    setNewJobPrefill(prefill);
    setNewJobOpen(true);
  }

  return (
    <PortalShell>
      <TopBar
        brand={<BrandMark label="Dispatch Center" sublabel={activeNav?.label || "Dispatcher"} />}
        user={info}
        onLogout={onLogout}
        onMenu={drawer.openMenu}
        right={
          <Button
            data-testid="dispatch-new-job-btn"
            onClick={() => openNewJob(null)}
            className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white font-semibold rounded-full h-9 px-3 sm:px-4"
          >
            <Plus className="w-4 h-4 sm:mr-1.5" />
            <span className="hidden sm:inline">New job</span>
          </Button>
        }
      />
      <MobileDrawer
        open={drawer.open}
        onClose={drawer.closeMenu}
        items={navWithCounts}
        active={tab}
        onSelect={setTab}
        brand={<BrandMark label="Dispatch Center" sublabel="Dispatcher" />}
        user={info}
        onLogout={onLogout}
      />
      <div className="flex">
        <Sidebar items={navWithCounts} active={tab} onSelect={setTab} />
        <PageContent>
          {tab === "board" && <Board key={reloadKey} info={info} onNewJob={openNewJob} />}
          {tab === "calendar" && <DispatchCalendar onJobClick={(id) => setCalJob(id)} onNewJob={openNewJob} />}
          {tab === "search" && <SearchPanel onNewJob={openNewJob} />}
          {tab === "callbacks" && <Callbacks onNewJob={openNewJob} />}
          {tab === "recurring" && <RecurringPlans />}
          {tab === "prices" && <Prices />}
        </PageContent>
      </div>

      {/* Controlled NewJobDialog — opened from any CTA throughout the dispatch UI */}
      <NewJobDialog
        open={newJobOpen}
        onOpenChange={setNewJobOpen}
        leadPrefill={newJobPrefill}
        onCreated={() => { setNewJobOpen(false); setReloadKey(k => k + 1); toast.success("Job created"); }}
        trigger={<span style={{ display: "none" }} />}
      />

      <FloatingComposer open={composerOpen} setOpen={setComposerOpen} />

      {/* Job drawer opened from the calendar */}
      <JobDrawer jobId={calJob} onClose={() => setCalJob(null)} onChanged={() => setReloadKey(k => k + 1)} role="dispatcher" />
    </PortalShell>
  );
}

// ============== Board ==============
function Board({ onNewJob }) {
  const [data, setData] = useState(null);
  const [selectedJob, setSelectedJob] = useState(null);
  const reload = () => apiStaff.get("/crm/jobs/dispatch").then(r => setData(r.data));
  useEffect(() => { reload(); }, []);
  useRealtime("staff", ["job."], reload);
  if (!data) return <Loading />;
  return (
    <>
      <PageHeader
        title="Dispatch board"
        subtitle={`${data.technicians.length} technicians · drag a job to reassign`}
        actions={
          <Button
            data-testid="dispatch-board-newjob"
            onClick={() => onNewJob(null)}
            className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white h-9 hidden sm:inline-flex"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New job
          </Button>
        }
      />
      <DispatchBoardDnD
        data={data}
        role="dispatcher"
        testidPrefix="d"
        onCardClick={setSelectedJob}
        onReload={reload}
      />
      <JobDrawer jobId={selectedJob} onClose={() => setSelectedJob(null)} onChanged={reload} role="dispatcher" />
    </>
  );
}

// ============== Search panel — find existing customers ==============
const STATUS_TONE = {
  new: "slate",
  scheduled: "blue",
  "en-route": "amber",
  "on-site": "amber",
  completed: "emerald",
  cancelled: "rose",
};

function SearchPanel({ onNewJob }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null); // null=initial, [] = no results
  const [selectedJob, setSelectedJob] = useState(null);

  // Debounced auto-search after 350ms of inactivity (only for queries 2+ chars)
  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      setResults(null);
      return;
    }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await apiStaff.get(`/crm/jobs/search?q=${encodeURIComponent(trimmed)}`);
        setResults(r.data || []);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Search failed");
        setResults([]);
      } finally {
        setBusy(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  // Group jobs by phone (digits-only key) to show customer history
  const customers = useMemo(() => {
    if (!results) return null;
    const map = new Map();
    for (const j of results) {
      const key = (j.customer_phone || "").replace(/\D+/g, "") || j.customer_name || j.id;
      const c = map.get(key) || {
        key,
        name: j.customer_name || "(unknown)",
        phone: j.customer_phone || "",
        email: j.customer_email || "",
        address: j.address || "",
        jobs: [],
      };
      c.jobs.push(j);
      // Prefer the most recent contact info (results are newest-first → first occurrence wins)
      if (!c.email && j.customer_email) c.email = j.customer_email;
      if (!c.address && j.address) c.address = j.address;
      map.set(key, c);
    }
    return Array.from(map.values());
  }, [results]);

  function makeRecall(customer) {
    // Prefill new job from latest job for this customer
    const latest = customer.jobs[0];
    onNewJob({
      id: undefined,
      name: customer.name,
      phone: customer.phone,
      email: customer.email,
      address: customer.address,
      appliance: latest?.appliance || "washer",
      issue: latest ? `Recall — previously: ${latest.appliance}. ${latest.diagnosis_note ? "Last visit: " + latest.diagnosis_note : ""}` : "",
    });
  }

  function makeFreshFor(customer) {
    onNewJob({
      id: undefined,
      name: customer.name,
      phone: customer.phone,
      email: customer.email,
      address: customer.address,
      appliance: "washer",
      issue: "",
    });
  }

  return (
    <>
      <PageHeader
        title="Search customers & job history"
        subtitle="While on a call — type phone, name, or address. Tap a result to book a recall or new visit."
      />

      {/* Big sticky search bar */}
      <div className="sticky top-2 z-10 mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#FF6600]" />
          <Input
            autoFocus
            data-testid="dispatch-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Phone, name, or address…"
            className="h-14 pl-11 pr-4 text-base sm:text-lg border-2 border-[#002060]/15 focus-visible:ring-[#FF6600] focus-visible:border-[#FF6600] bg-[#FFFFFF] shadow-sm rounded-xl"
          />
          {q && (
            <button
              data-testid="dispatch-search-clear"
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#002060]/40 hover:text-[#FF6600] p-1"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="text-xs text-[#002060]/60 mt-1.5 px-1">
          Tip: type just the last 4 digits of a phone — searches across formatting (e.g. "210-555-1234", "(210) 555 1234", "2105551234").
        </p>
      </div>

      {q.trim().length < 2 && (
        <Card>
          <EmptyState
            icon={Search}
            message="Start typing to find a customer."
            hint="Most calls are repeat customers — search by phone first."
          />
        </Card>
      )}

      {busy && q.trim().length >= 2 && <div className="text-sm text-[#002060]/70 px-1">Searching…</div>}

      {!busy && customers && customers.length === 0 && (
        <Card>
          <EmptyState
            icon={Search}
            message="No customer matches that search."
            hint="They might be brand new — tap 'New job' to start fresh."
          />
          <div className="mt-3 flex justify-center">
            <Button
              data-testid="dispatch-search-newjob-empty"
              onClick={() => onNewJob(null)}
              className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white"
            >
              <Plus className="w-4 h-4 mr-1.5" /> New job for new customer
            </Button>
          </div>
        </Card>
      )}

      {!busy && customers && customers.length > 0 && (
        <div className="space-y-3">
          {customers.map((c) => (
            <CustomerHistoryCard
              key={c.key}
              customer={c}
              onMakeRecall={() => makeRecall(c)}
              onMakeFresh={() => makeFreshFor(c)}
              onOpenJob={setSelectedJob}
            />
          ))}
        </div>
      )}

      <JobDrawer jobId={selectedJob} onClose={() => setSelectedJob(null)} role="dispatcher" />
    </>
  );
}

function CustomerHistoryCard({ customer, onMakeRecall, onMakeFresh, onOpenJob }) {
  const [expanded, setExpanded] = useState(customer.jobs.length <= 2);
  const visible = expanded ? customer.jobs : customer.jobs.slice(0, 2);
  return (
    <div className="bg-[#FFFFFF] border border-[#002060]/15 rounded-xl overflow-hidden hover:border-[#002060]/30 transition-colors shadow-sm">
      {/* Customer header */}
      <div className="p-4 border-b border-[#002060]/10 bg-[#002060]/[0.02]">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-full bg-[#FF6600]/10 text-[#FF6600] grid place-items-center font-bold text-base shrink-0">
            {(customer.name || "?").trim().charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-[#002060] text-base truncate" data-testid={`search-cust-name-${customer.key}`}>
              {customer.name}
            </div>
            <div className="mt-1 space-y-0.5">
              {customer.phone && (
                <a
                  href={`tel:${customer.phone}`}
                  className="flex items-center gap-1.5 text-sm text-[#002060]/80 hover:text-[#FF6600] num"
                  data-testid={`search-cust-call-${customer.key}`}
                >
                  <PhoneCall className="w-3.5 h-3.5 text-[#FF6600] shrink-0" /> {customer.phone}
                </a>
              )}
              {customer.address && (
                <div className="flex items-center gap-1.5 text-sm text-[#002060]/70 truncate">
                  <MapPin className="w-3.5 h-3.5 text-[#FF6600] shrink-0" />
                  <span className="truncate">{customer.address}</span>
                </div>
              )}
              {customer.email && (
                <div className="flex items-center gap-1.5 text-xs text-[#002060]/60 truncate">
                  <Mail className="w-3 h-3 shrink-0" />
                  <span className="truncate">{customer.email}</span>
                </div>
              )}
            </div>
          </div>
          <span className="shrink-0 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full border border-[#002060]/20 text-[#002060]/70">
            {customer.jobs.length} {customer.jobs.length === 1 ? "job" : "jobs"}
          </span>
        </div>

        {/* CTAs — primary actions while on the phone */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <Button
            data-testid={`search-recall-${customer.key}`}
            onClick={onMakeRecall}
            className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white h-10 font-semibold"
          >
            <Repeat className="w-4 h-4 mr-1.5" /> Schedule recall
          </Button>
          <Button
            data-testid={`search-newvisit-${customer.key}`}
            onClick={onMakeFresh}
            variant="outline"
            className="border-[#002060]/30 text-[#002060] hover:bg-[#FF6600]/10 hover:border-[#FF6600] h-10 font-semibold"
          >
            <Plus className="w-4 h-4 mr-1.5" /> New visit
          </Button>
        </div>
      </div>

      {/* Job history */}
      <div className="divide-y divide-[#002060]/10">
        {visible.map((j) => (
          <button
            key={j.id}
            onClick={() => onOpenJob(j.id)}
            data-testid={`search-job-${j.id}`}
            className="w-full text-left p-3 hover:bg-[#FF6600]/5 transition-colors flex items-center gap-3"
          >
            <Clock className="w-3.5 h-3.5 text-[#002060]/40 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium text-[#002060] capitalize">{j.appliance}</span>
                <StatusPill tone={STATUS_TONE[j.status] || "slate"} label={j.status} />
              </div>
              <div className="text-xs text-[#002060]/60 mt-0.5 truncate">
                {(j.issue || j.diagnosis_note || "—").slice(0, 90)}
              </div>
            </div>
            <div className="text-xs text-[#002060]/50 shrink-0">
              {j.scheduled_for ? new Date(j.scheduled_for).toLocaleDateString() : new Date(j.created_at).toLocaleDateString()}
            </div>
            <ChevronRight className="w-4 h-4 text-[#002060]/30 shrink-0" />
          </button>
        ))}
        {customer.jobs.length > 2 && (
          <button
            onClick={() => setExpanded((v) => !v)}
            data-testid={`search-expand-${customer.key}`}
            className="w-full text-xs text-[#FF6600] font-semibold py-2 hover:bg-[#FF6600]/5 uppercase tracking-widest flex items-center justify-center gap-1.5"
          >
            <History className="w-3.5 h-3.5" />
            {expanded ? "Hide history" : `Show all ${customer.jobs.length} jobs`}
          </button>
        )}
      </div>
    </div>
  );
}

// ============== Callbacks ==============
function Callbacks({ onNewJob }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");
  const [form, setForm] = useState({ phone: "", customer_name: "", note: "" });
  const reload = () => apiStaff.get(`/crm/missed-calls?status=${filter}`).then(r => setItems(r.data));
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  async function add() {
    if (!form.phone) return toast.error("Phone required");
    try {
      await apiStaff.post("/crm/missed-calls", form);
      toast.success("Added to queue");
      setForm({ phone: "", customer_name: "", note: "" });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }
  async function resolve(id) { await apiStaff.post(`/crm/missed-calls/${id}/resolve`); reload(); }
  function convertToJob(cb) {
    onNewJob && onNewJob({
      id: undefined,
      name: cb.customer_name || "",
      phone: cb.phone || "",
      address: "",
      appliance: "washer",
      issue: cb.note || "",
    });
  }

  return (
    <>
      <PageHeader
        title="Callback queue"
        subtitle={`${items.length} ${filter}`}
        actions={
          <div className="inline-flex bg-[#FFFFFF] border border-[#002060]/15 rounded-md p-0.5 text-xs">
            {["pending", "resolved", "all"].map(s => (
              <button
                key={s}
                data-testid={`cb-filter-${s}`}
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded capitalize transition-colors ${
                  filter === s ? "bg-[#FF6600] text-white" : "text-[#002060]/80 hover:bg-[#FFFFFF]"
                }`}
              >{s}</button>
            ))}
          </div>
        }
      />
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-2.5">
          {items.length === 0 ? (
            <Card><EmptyState message={`No ${filter} callbacks.`} /></Card>
          ) : (
            items.map(cb => (
              <div key={cb.id} className="bg-[#FFFFFF] border border-[#002060]/15 rounded-lg p-4 hover:border-[#002060]/30 transition-colors">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-[#002060] truncate">{cb.customer_name || "Unknown"} · <span className="num">{cb.phone}</span></div>
                    <div className="text-xs text-[#002060]/70 mt-0.5 line-clamp-2">{cb.note || "—"}</div>
                    <div className="text-[11px] text-[#002060]/50 mt-1">{new Date(cb.created_at).toLocaleString()}</div>
                  </div>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <a href={`tel:${cb.phone}`}>
                    <Button size="sm" data-testid={`cb-call-${cb.id}`} className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white h-9">
                      <PhoneCall className="w-3.5 h-3.5 mr-1.5"/> Call
                    </Button>
                  </a>
                  <Button
                    size="sm"
                    onClick={() => convertToJob(cb)}
                    data-testid={`cb-book-${cb.id}`}
                    variant="outline"
                    className="h-9 border-[#FF6600] text-[#FF6600] hover:bg-[#FF6600]/10"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1.5" /> Book job
                  </Button>
                  {cb.status === "pending" && (
                    <Button size="sm" variant="outline" onClick={() => resolve(cb.id)} data-testid={`cb-resolve-${cb.id}`} className="h-9 border-[#002060]/30 text-[#002060]">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Resolve
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
        <Card title="Log missed call">
          <p className="text-xs text-[#002060]/70 -mt-2 mb-4">Manually add a call to the queue.</p>
          <div className="space-y-3">
            <Mini label="Phone"><Input data-testid="cb-add-phone" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} /></Mini>
            <Mini label="Customer name"><Input data-testid="cb-add-name" value={form.customer_name} onChange={(e) => setForm({...form, customer_name: e.target.value})} /></Mini>
            <Mini label="Note"><Textarea rows={2} data-testid="cb-add-note" value={form.note} onChange={(e) => setForm({...form, note: e.target.value})} /></Mini>
            <Button data-testid="cb-add-submit" onClick={add} className="w-full bg-[#FF6600] hover:bg-[#FF6600]/90 text-white h-9">Add to queue</Button>
          </div>
        </Card>
      </div>
    </>
  );
}

// ============== Prices (read-only) ==============
function Prices() {
  const [bands, setBands] = useState([]);
  useEffect(() => { apiStaff.get("/pricing/bands").then(r => setBands(r.data)); }, []);
  return (
    <>
      <PageHeader title="Price guide" subtitle="Quote ranges only · final pricing set by manager / tech" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {bands.map(b => (
          <div key={b.appliance} className="bg-[#FFFFFF] border border-[#002060]/15 rounded-lg p-4">
            <div className="font-medium capitalize">{b.appliance}</div>
            <div className="display text-2xl font-semibold num text-[#002060] mt-1">${b.low}<span className="text-[#002060]/50">–</span>${b.high}</div>
            {b.note && <div className="text-xs text-[#002060]/70 mt-2 line-clamp-2">{b.note}</div>}
          </div>
        ))}
      </div>
      {bands.length === 0 && <Card><EmptyState message="No pricing bands configured." /></Card>}
    </>
  );
}

// ============== Mini field ==============
function Mini({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-[#002060]/70 font-medium">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// ============== Floating chat-style composer (10-step intake) ==============
const STEPS = [
  { k: "customer_name", q: "What's the customer's name?", ph: "First Last" },
  { k: "customer_phone", q: "Phone number?", ph: "(210) 555-0199" },
  { k: "customer_email", q: "Email (optional, type 'skip' to skip)", ph: "you@example.com" },
  { k: "address", q: "Service address?", ph: "123 Main St, San Antonio, TX" },
  { k: "appliance", q: "Which appliance?", ph: APPLIANCES.join(" / "), choices: APPLIANCES },
  { k: "issue", q: "What's the issue?", ph: "Fridge not cooling, etc." },
  { k: "preferred_window", q: "Preferred window?", ph: "Today PM / Tue 8-10am" },
  { k: "scheduled_for", q: "Schedule ISO time (optional, type 'skip')", ph: "2026-06-03T10:00:00Z" },
  { k: "technician_id", q: "Assign tech? (name or type 'skip')", ph: "Tech name or skip" },
  { k: "private_tech_note", q: "Any private note for the tech? (or skip)", ph: "Buzz at the gate" },
];

function FloatingComposer({ open, setOpen }) {
  const [techs, setTechs] = useState([]);
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [input, setInput] = useState("");
  const [history, setHistory] = useState([]);

  useEffect(() => { apiStaff.get("/technicians").then(r => setTechs(r.data)); }, []);
  useEffect(() => {
    if (open && window.__leadPrefill) {
      const l = window.__leadPrefill;
      setAnswers({
        customer_name: l.name, customer_phone: l.phone, customer_email: l.email || "skip",
        address: l.address || "", appliance: l.appliance, issue: l.issue || "skip",
        lead_id: l.id,
      });
      setHistory([{ from: "bot", text: `Pre-filled from lead "${l.name}". Continuing at step 7…` }]);
      setStep(6);
      delete window.__leadPrefill;
    }
  }, [open]);

  const current = STEPS[step];

  async function send() {
    if (!current) return;
    const val = input.trim();
    if (!val) return;
    setHistory(h => [...h, { from: "you", text: val }, { from: "bot", text: STEPS[step + 1]?.q || "Creating job…" }]);
    setAnswers(a => ({ ...a, [current.k]: val.toLowerCase() === "skip" ? "" : val }));
    setInput("");
    if (step + 1 >= STEPS.length) {
      await create({ ...answers, [current.k]: val.toLowerCase() === "skip" ? "" : val });
    } else {
      setStep(step + 1);
    }
  }

  async function create(a) {
    const payload = {
      customer_name: a.customer_name,
      customer_phone: a.customer_phone,
      address: a.address,
      appliance: a.appliance,
    };
    if (a.customer_email) payload.customer_email = a.customer_email;
    if (a.issue) payload.issue = a.issue;
    if (a.preferred_window) payload.scheduled_for = a.preferred_window;
    if (a.scheduled_for) payload.scheduled_for = a.scheduled_for;
    if (a.private_tech_note) payload.private_tech_note = a.private_tech_note;
    if (a.lead_id) payload.lead_id = a.lead_id;
    if (a.technician_id && a.technician_id !== "skip") {
      const t = techs.find(x => x.name.toLowerCase().includes(String(a.technician_id).toLowerCase()) || x.id === a.technician_id);
      if (t) payload.technician_id = t.id;
    }
    try {
      await apiStaff.post("/crm/jobs", payload);
      toast.success("Job created");
      setHistory(h => [...h, { from: "bot", text: "Job created. Closing in a moment." }]);
      setTimeout(() => { setOpen(false); setStep(0); setAnswers({}); setHistory([]); }, 1000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      setHistory(h => [...h, { from: "bot", text: (e.response?.data?.detail || "Failed") }]);
    }
  }

  return (
    <>
      {!open && (
        <button
          onClick={() => { setOpen(true); setStep(0); setAnswers({}); setHistory([{ from: "bot", text: STEPS[0].q }]); }}
          data-testid="composer-open-btn"
          className="fixed bottom-6 right-6 z-40 bg-[#FF6600] hover:bg-[#FF6600] text-white rounded-full w-14 h-14 grid place-items-center shadow-lg hover:scale-105 transition"
          aria-label="Open chat-style job intake"
          title="Chat-style intake (alternative)"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[380px] max-w-[calc(100vw-2rem)] bg-[#FFFFFF] border border-[#002060]/15 rounded-xl shadow-2xl flex flex-col" style={{ height: 520 }}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#002060]/15 bg-[#FFFFFF] rounded-t-xl">
            <div>
              <div className="display text-sm font-semibold text-[#002060]">Chat intake</div>
              <div className="text-[11px] text-[#002060]/70">Step {Math.min(step + 1, STEPS.length)} of {STEPS.length}</div>
            </div>
            <button data-testid="composer-close" onClick={() => setOpen(false)} className="text-[#002060]/50 hover:text-[#002060] transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-2 flex flex-col">
            {history.map((m, i) => (
              <div
                key={i}
                className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${
                  m.from === "bot"
                    ? "bg-[#FFFFFF] text-[#002060] self-start rounded-bl-sm border border-[#002060]/15"
                    : "bg-[#FF6600] text-white self-end rounded-br-sm"
                }`}
              >
                {m.text}
              </div>
            ))}
          </div>
          <div className="border-t border-[#002060]/15 p-3 flex gap-2">
            <Input
              data-testid="composer-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={current?.ph || "..."}
              className="h-9"
            />
            <Button data-testid="composer-send" onClick={send} className="bg-[#FF6600] hover:bg-[#FF6600]/90 text-white h-9">Send</Button>
          </div>
        </div>
      )}
    </>
  );
}
